/* eslint-disable */
import mixpanel from 'mixpanel-browser';
import { APP_CONFIG } from '../config/envConfig';

const MIXPANEL_TOKEN = APP_CONFIG.mixpanelToken;

let initialized = false;

/** Exact hosts that share Mixpanel identity via a root-domain cookie. */
const MIXPANEL_CROSS_SUBDOMAIN_HOSTS = {
  // Prod
  'thewhiteowl.ai': '.thewhiteowl.ai', // website
  'app.thewhiteowl.ai': '.thewhiteowl.ai', // app
  // Staging
  'webstaging.thewhiteowl.ai': '.thewhiteowl.ai', // website
  'staging.thewhiteowl.ai': '.thewhiteowl.ai', // app
};

/** Share anonymous distinct_id across website + app on the same root domain. */
function getMixpanelPersistenceConfig() {
  if (typeof window === 'undefined') {
    return { persistence: 'localStorage' };
  }
  const host = window.location.hostname || '';
  if (host === 'localhost' || host === '127.0.0.1') {
    // Custom cookie_domain cannot be set on localhost.
    return { persistence: 'localStorage' };
  }
  const cookieDomain = MIXPANEL_CROSS_SUBDOMAIN_HOSTS[host];
  if (cookieDomain) {
    return {
      persistence: 'cookie',
      cross_subdomain_cookie: true,
      cookie_domain: cookieDomain,
    };
  }
  return { persistence: 'localStorage' };
}

function ensureInit() {
  if (initialized) return;
  if (!MIXPANEL_TOKEN) return;
  mixpanel.init(MIXPANEL_TOKEN, {
    autocapture: false,
    record_sessions_percent: 100,
    ...getMixpanelPersistenceConfig(),
  });
  initialized = true;
  // Single Mixpanel project shared with the app (see MIXPANEL_CROSS_SUBDOMAIN_HOSTS
  // above) - "platform" is what separates website vs product events in every report.
  guarded(() => mixpanel.register({ platform: 'website' }));
  enableActionClickTracking();
}

// Public IPs to exclude from Mixpanel entirely (e.g. the office).
const OFFICE_IPS = [];

let officeCheckPromise = null;

async function fetchVisitorIp() {
  try {
    const res = await fetch('https://api.ipify.org?format=json');
    const data = await res.json();
    return data.ip;
  } catch (_) {
    return null;
  }
}

const IP_CACHE_KEY = 'mp_office_ip_check';
const IP_CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

function readIpCache() {
  try {
    const parsed = JSON.parse(localStorage.getItem(IP_CACHE_KEY));
    if (!parsed || typeof parsed.isOffice !== 'boolean' || typeof parsed.checkedAt !== 'number') {
      return null;
    }
    if (Date.now() - parsed.checkedAt >= IP_CACHE_TTL_MS) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function writeIpCache(isOffice) {
  try {
    localStorage.setItem(IP_CACHE_KEY, JSON.stringify({ isOffice, checkedAt: Date.now() }));
  } catch (_) {
    // localStorage unavailable — skip caching.
  }
}

function isOfficeVisitor() {
  if (!officeCheckPromise) {
    officeCheckPromise = (async () => {
      if (OFFICE_IPS.length === 0) return false;

      const cached = readIpCache();
      if (cached) return cached.isOffice;

      const ip = await fetchVisitorIp();
      const isOffice = ip != null && OFFICE_IPS.includes(ip);
      if (ip != null) writeIpCache(isOffice);
      return isOffice;
    })();
  }
  return officeCheckPromise;
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    officeCheckPromise = null;
    try {
      localStorage.removeItem(IP_CACHE_KEY);
    } catch (_) {}
  });
}

async function guarded(sendFn) {
  if (await isOfficeVisitor()) return;
  sendFn();
}

const CLICKABLE_SELECTOR =
  'button, a, [role="button"], [role="link"], [role="menuitem"], [role="tab"], input[type="button"], input[type="submit"], [data-mp-label]';

function getActionLabel(el) {
  const explicit = el.getAttribute('data-mp-label');
  if (explicit && explicit.trim()) return explicit.trim();

  const ariaLabel = el.getAttribute('aria-label');
  if (ariaLabel && ariaLabel.trim()) return ariaLabel.trim();

  const title = el.getAttribute('title');
  if (title && title.trim()) return title.trim();

  const text = (el.innerText || el.textContent || '').trim().replace(/\s+/g, ' ');
  if (text) return text.slice(0, 60);

  return null;
}

const MODAL_CONTAINER_SELECTOR = '[role="dialog"], [aria-modal="true"], [class*="modal" i]';

function findModalContext(el) {
  const container = el.closest(MODAL_CONTAINER_SELECTOR);
  if (!container) return null;

  const containerLabel = container.getAttribute('aria-label');
  if (containerLabel && containerLabel.trim()) return containerLabel.trim();

  const titleEl = container.querySelector('h1, h2, h3, h4, [class*="title" i]');
  if (titleEl) {
    const text = (titleEl.innerText || titleEl.textContent || '').trim().replace(/\s+/g, ' ');
    if (text) return text.slice(0, 60);
  }

  return null;
}

function enableActionClickTracking() {
  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;
      const el = event.target.closest(CLICKABLE_SELECTOR);
      if (!el) return;

      const label = getActionLabel(el);
      if (!label) return;

      const modalContext = findModalContext(el);

      guarded(() =>
        mixpanel.track(`Clicked on ${label}`, {
          label,
          page: window.location.pathname,
          full_url: window.location.href,
          ...(modalContext ? { modal: modalContext } : {}),
        })
      );
    },
    true
  );
}

/**
 * Fire for the site's primary CTAs only (per taxonomy §2: Book a Demo, Sign
 * Up, Talk to Us) - call explicitly at the CTA's onClick, not automatically.
 */
export function trackCtaClicked(ctaLabel, { position, page } = {}) {
  ensureInit();
  guarded(() =>
    mixpanel.track('CTA Clicked', {
      cta_label: ctaLabel,
      page: page ?? window.location.pathname,
      position,
    })
  );
}

function resolveUserRecord(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const nested = payload.data;
  const nestedIsUser =
    nested
    && typeof nested === 'object'
    && !Array.isArray(nested)
    && (nested.email != null || nested.id != null || nested.user_id != null || nested.pk != null);
  if (nestedIsUser) return nested;
  if (
    payload.email != null
    || payload.id != null
    || payload.user_id != null
    || payload.pk != null
  ) {
    return payload;
  }
  return null;
}

function resolveDistinctId(user) {
  if (!user) return null;
  const raw = user.id ?? user.user_id ?? user.pk ?? user.uuid ?? user.email;
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (user.email != null && String(raw) === String(user.email)) {
    return s.toLowerCase();
  }
  return s;
}

/**
 * @param {object} loginPayload
 * @param {{ trackLoginEvent?: boolean }} options
 */
export function syncMixpanelUser(loginPayload, options = {}) {
  ensureInit();
  const user = resolveUserRecord(loginPayload);
  const distinctId = resolveDistinctId(user);
  if (!user || !distinctId) return;

  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  guarded(() => {
    mixpanel.identify(distinctId);
    mixpanel.people.set({
      $name: name || undefined,
      $email: user.email,
      role: user.user_type ?? user.role,
    });
    if (options.trackLoginEvent) {
      mixpanel.track('Logged In', { login_method: options.loginMethod ?? 'unknown' });
    }
  });
}

export function resetMixpanel() {
  ensureInit();
  mixpanel.reset();
}

/**
 * Fire once, at the moment an account first becomes real - see taxonomy §3/§4
 * and the longer note in the app's mixpanelClient.js. Not currently called
 * from this repo: every signup path here (EarlyAccessForm.js, Onboarding.js)
 * hands off to the app (zap-react) via exchange_code or an emailed
 * verification link, and the app is where the account actually becomes real
 * - so trackSignupCompleted() lives and fires there, not here. Exported for
 * symmetry / in case a website-only completion path is ever added.
 *
 * @param {{ signupMethod?: string }} options
 */
export function trackSignupCompleted(options = {}) {
  ensureInit();
  guarded(() => {
    const signupSource = mixpanel.get_property('first_touch_utm_source') ?? null;
    const signupCampaign = mixpanel.get_property('first_touch_utm_campaign') ?? null;
    mixpanel.people.set({ signup_source: signupSource, signup_campaign: signupCampaign });
    const props = {
      signup_source: signupSource,
      signup_campaign: signupCampaign,
      signup_method: options.signupMethod ?? 'unknown',
    };
    mixpanel.track('Signup Completed', props);
    mixpanel.track('Account Created', props);
  });
}

/** Fires on signup form submit / OAuth signup button click, before the API call. */
export function trackSignupStarted(signupMethod) {
  ensureInit();
  guarded(() =>
    mixpanel.track('Signup Started', {
      page: window.location.pathname,
      signup_method: signupMethod ?? 'unknown',
    })
  );
}

/** Demo/contact form submitted. */
export function trackDemoRequested({ page } = {}) {
  ensureInit();
  guarded(() =>
    mixpanel.track('Demo Requested', {
      page: page ?? window.location.pathname,
      utm_source: mixpanel.get_property('utm_source') ?? null,
    })
  );
}

const PAGE_LABEL_RULES = [
  [/^\/$/, 'Home'],
  [/^\/pricing$/i, 'Pricing'],
  [/^\/NewHome$/, 'Home Legacy'],
  [/^\/payment\/success/, 'Payment Success'],
  [/^\/onboarding/, 'Onboarding'],
  [/^\/privacy/, 'Privacy Policy'],
  [/^\/terms/, 'Terms of Use'],
  [/^\/category/, 'Category'],
  [/^\/about/, 'About'],
  [/^\/blog/, 'Blog'],
];

function isDynamicSegment(segment) {
  return /^\d+$/.test(segment) || /^[0-9a-f-]{8,}$/i.test(segment);
}

function humanizeSegment(segment) {
  return segment
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function fallbackPageLabel(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  for (let i = segments.length - 1; i >= 0; i--) {
    if (!isDynamicSegment(segments[i])) {
      return humanizeSegment(segments[i]);
    }
  }
  return 'Home';
}

function getPageLabel(pathname) {
  const rule = PAGE_LABEL_RULES.find(([regex]) => regex.test(pathname));
  if (rule) return rule[1];
  return fallbackPageLabel(pathname);
}

export function trackPageView(pathname) {
  ensureInit();
  const label = getPageLabel(pathname);
  guarded(() =>
    mixpanel.track('Page Viewed', {
      page: label,
      path: pathname,
      full_url: window.location.href,
    })
  );
}

export function trackEvent(name, props = {}) {
  ensureInit();
  guarded(() =>
    mixpanel.track(name, {
      page: window.location.pathname,
      full_url: window.location.href,
      ...props,
    })
  );
}

const UTM_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
];

export function captureUtmSource() {
  ensureInit();
  const params = new URLSearchParams(window.location.search);
  const utmData = Object.fromEntries(
    UTM_PARAM_KEYS.map((key) => [key, params.get(key)]).filter(([, v]) => v)
  );

  guarded(() => {
    // register_once() only writes a key once per visitor, and only overwrites
    // later if the current value matches its no-value sentinel - so only UTM
    // keys actually present get included here (a null on a UTM-less first
    // visit would otherwise permanently lock out a real value later).
    const firstTouchUtm = Object.fromEntries(
      ['utm_source', 'utm_medium', 'utm_campaign']
        .map((key) => [`first_touch_${key}`, params.get(key)])
        .filter(([, v]) => v)
    );
    if (Object.keys(firstTouchUtm).length > 0) {
      mixpanel.register_once(firstTouchUtm);
      mixpanel.people.set_once(firstTouchUtm);
    }

    const firstTouchContext = {
      first_touch_referrer: document.referrer || 'direct',
      first_touch_landing_page: window.location.pathname,
    };
    mixpanel.register_once(firstTouchContext);
    mixpanel.people.set_once(firstTouchContext);

    if (Object.keys(utmData).length > 0) {
      mixpanel.register(utmData);
      mixpanel.people.set(utmData);
    }
  });
}
